import { Storage } from 'aws-amplify'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { JSONTree } from 'react-json-tree'
import { getPlannedNewHiresPerYear } from '../../constants/planned-new-hires-per-year'
import {
  useAivyContext,
  useNotificationContext,
  usePartnerContext,
  useProductTourContext,
  useUserContext
} from '../../context'
import { mutation } from '../../graphql'
import { notifyBugsnag } from '../../helpers'
import { useMount } from '../../hooks/use-mount'
import ImageCropAssessment from '../image-crop-assessment'
import Input from '../input'
import SelectMenu from '../select-menu'
import SEO from '../seo'
import UploadPicture from '../upload-picture'
import CulturalFitCompany from './company-profile/cultural-fit-company'
import CustomPrivacyLinkSetting from './company-profile/custom-privacy-link-setting'
import SettingsContainer from './settings-container'

const TRANSLATION_LOCATION = 'components.settings.company_profile.'

const CompanyProfile = () => {
  const { t } = useTranslation()
  const { success, error } = useNotificationContext()
  const { adminInsights } = useAivyContext()
  const { partner, refetchPartner } = usePartnerContext()
  const { refetchEmployees } = useUserContext()

  const {
    setProductTourState,
    productTourState: { tourActive }
  } = useProductTourContext()

  useMount(() => {
    if (tourActive) {
      setTimeout(() => {
        setProductTourState({ run: true })
      }, 0.4 * 1000)
    }
  })

  const [isLoading, setIsLoading] = useState(false)
  const [partnerLogo, setPartnerLogo] = useState(partner.logo)
  const [
    plannedNewHiresPerYearMandatoryHint,
    setPlannedNewHiresPerYearMandatoryHint
  ] = useState(false)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: partner.name || '',
      displayName: partner.display_name || '',
      careerSiteUrl: partner.career_site_url || '',
      plannedNewHiresPerYear: partner.planned_new_hires_per_year || -1
    },
    validationSchema: Yup.object({
      name: Yup.string().required(
        t(TRANSLATION_LOCATION + 'input_name_validation')
      ),
      displayName: Yup.string().required(
        t(TRANSLATION_LOCATION + 'input_display_name_validation')
      ),
      careerSiteUrl: Yup.string().url(
        t(TRANSLATION_LOCATION + 'input_career_site_url_validation')
      )
    }),
    onSubmit: async (values, helpers) => {
      // if (!partnerLogo) return
      if (formik.values.plannedNewHiresPerYear < 0) return

      setIsLoading(true)

      try {
        await mutation({
          mutation: 'updatePartner',
          input: {
            id: partner.id,
            name: values.name,
            display_name: values.displayName,
            career_site_url: values.careerSiteUrl,
            planned_new_hires_per_year: values.plannedNewHiresPerYear
          }
        })

        success(t(TRANSLATION_LOCATION + 'update_partner_success'))

        await refetchPartner()
        await refetchEmployees()
      } catch (err) {
        helpers.resetForm()
      } finally {
        setIsLoading(false)
      }
    }
  })

  useEffect(() => {
    if (formik.isSubmitting && formik.values.plannedNewHiresPerYear < 0) {
      setPlannedNewHiresPerYearMandatoryHint(
        t(TRANSLATION_LOCATION + 'empty_planned_new_hires_per_year_hint')
      )
    }
  }, [formik.isSubmitting, formik.values.plannedNewHiresPerYear, t])

  const updateURL = ({ values }) => {
    if (values.careerSiteUrl && !values.careerSiteUrl.startsWith('http')) {
      formik.setFieldValue(
        'careerSiteUrl',
        ['https://', values.careerSiteUrl].join('')
      )
    }
  }

  const uploadLogo = async ({ image }) => {
    if (!image) {
      error(t(TRANSLATION_LOCATION + 'update_partner_logo_error'))
      return
    }

    setIsLoading(true)

    try {
      const extension = image.name.split('.').pop()

      if (
        !extension ||
        !['jpg', 'jpeg', 'png', 'webp'].includes(extension.toLowerCase())
      ) {
        throw new Error('Invalid file extension')
      }

      const result = await Storage.put(
        `partner-logos/${partner.id}.${extension}`,
        image,
        {
          contentType: image.type,
          level: 'public'
        }
      )

      if (!result || !result.key) {
        throw new Error('Storage upload failed')
      }

      const updatedPartnerLogo = (await Storage.get(result.key)).split('?')[0]

      await mutation({
        mutation: 'updatePartner',
        input: {
          id: partner.id,
          logo: updatedPartnerLogo
        }
      })

      await refetchPartner()
      await refetchEmployees()

      setPartnerLogo(updatedPartnerLogo)
      success(t(TRANSLATION_LOCATION + 'update_partner_logo_success'))
    } catch (err) {
      console.error('Logo upload error:', err)

      let errorMessage = t(TRANSLATION_LOCATION + 'update_partner_logo_error')

      if (err.message?.includes('Network')) {
        errorMessage = t(
          TRANSLATION_LOCATION + 'update_partner_logo_network_error'
        )
      } else if (err.message?.includes('size')) {
        errorMessage = t(
          TRANSLATION_LOCATION + 'update_partner_logo_size_error'
        )
      }

      error(errorMessage)
      notifyBugsnag(err, { context: 'uploadLogo', partnerId: partner.id })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <SEO title={t('seo.titles.settings_company')} />
      {adminInsights && <JSONTree data={partner} />}
      <SettingsContainer
        title={t(TRANSLATION_LOCATION + 'settings_container_title')}
        description={t(TRANSLATION_LOCATION + 'settings_container_description')}
        onSubmit={formik.handleSubmit}
        dirty={formik.dirty}
        isLoading={isLoading}
      >
        <div className='flex flex-col gap-y-4'>
          {[
            {
              id: 'name',
              label: t(TRANSLATION_LOCATION + 'input_name_label'),
              description: t(TRANSLATION_LOCATION + 'input_name_description')
            },
            {
              id: 'displayName',
              label: t(TRANSLATION_LOCATION + 'input_display_name_label'),
              description: t(
                TRANSLATION_LOCATION + 'input_display_name_description'
              )
            },
            {
              id: 'careerSiteUrl',
              label: t(TRANSLATION_LOCATION + 'input_career_site_url_label'),
              description: t(
                TRANSLATION_LOCATION + 'input_career_site_url_description'
              )
            }
          ].map(({ id, label, description }, index) => (
            <div key={index} className='max-w-lg'>
              <Input
                id={id}
                label={label}
                type='text'
                description={description}
                onChange={formik.handleChange}
                onBlur={(event) =>
                  formik.handleBlur(event) || updateURL(formik)
                }
                touched={formik.touched[id]}
                value={formik.values[id]}
                error={formik.errors[id]}
              />
            </div>
          ))}
          <SelectMenu
            className='max-w-sm'
            id='plannedNewHiresPerYear'
            label={t(TRANSLATION_LOCATION + 'planned_new_hires_per_year_label')}
            description={t(
              TRANSLATION_LOCATION + 'planned_new_hires_per_year_description'
            )}
            options={getPlannedNewHiresPerYear()}
            onChange={(value) => {
              setPlannedNewHiresPerYearMandatoryHint(false)
              formik.setFieldValue(
                'plannedNewHiresPerYear',
                parseInt(value) || -1
              )
            }}
            defaultValue={formik.values.plannedNewHiresPerYear}
            useOptionValue='relation'
          />
          {plannedNewHiresPerYearMandatoryHint && (
            <p className='mt-2 text-sm text-red-600'>
              {plannedNewHiresPerYearMandatoryHint}
            </p>
          )}
        </div>

        <div className='mt-4'>
          <UploadPicture
            title={t(TRANSLATION_LOCATION + 'input_partner_logo_label')}
            description={t(TRANSLATION_LOCATION + 'input_partner_logo_hint')}
            imageSrc={partnerLogo}
            onSubmit={uploadLogo}
            ImageCropComponent={ImageCropAssessment}
          />
        </div>
      </SettingsContainer>
      <div className='mt-12' />
      <CustomPrivacyLinkSetting />
      <div className='mt-12' />
      <CulturalFitCompany />
    </>
  )
}

export default CompanyProfile
